import React from 'react';
import { createSelector } from 'reselect';
import FormControl from '@material-ui/core/FormControl';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import FormHelperText from '@material-ui/core/FormHelperText';
import LinearProgress from '@material-ui/core/LinearProgress';
import { connect } from 'react-redux';
import lodash from 'lodash';
import { IRootState } from '../../../../../@types/redux';
import { IGeoServerBlock } from '../../../../../types/model/masterData/geoserver/block';
import MasterDataGeoServerFunctions from '../../../../../store/masterData/geoserver/functions';
import { LngLatBoundsLike } from 'react-map-gl';
import GeoHelper from '../../../../../services/helper/geoHelper';
import { IGeoServerPoint, pointType } from '../../../../../types/model/masterData/geoserver/point';
import { CROP } from '../../../../../appConstants';

interface IGeoServerBlockAutocompleteProps {
    id ?: string;
    value ?: string | null;

    onChange : (value ?: IGeoServerBlock, bounds ?: LngLatBoundsLike | null, event ?: React.ChangeEvent<unknown>) => void;

    blocks : Array<IGeoServerBlock>;
    points : Array<IGeoServerPoint>;

    required ?: boolean;
    autoFocus ?: boolean;

    fullWidth ?: boolean;

    isLoading : boolean;
    disabled ?: boolean;
    name ?: string;

    label ?: string;

    divisionGuid ?: string | null;
    subDivisionGuid ?: string | null;
    departmentGuid ?: string | null;
    fieldGuid ?: string | null;

    division ?: string | null;
    fieldName ?: string | null;
    crop ?: CROP | null;

    type : pointType | null;
}

interface IGeoServerBlockAutocompleteState {
}

class GeoServerBlockAutocompleteComponent extends React.Component<IGeoServerBlockAutocompleteProps, IGeoServerBlockAutocompleteState> {
    constructor(props : IGeoServerBlockAutocompleteProps) {
        super(props);

        this.state = {
        };
    }

    public readonly componentDidMount = () => {
        this.loadData();
    };

    public readonly loadData = async () => {
        MasterDataGeoServerFunctions.getBlockList();
        MasterDataGeoServerFunctions.getPointList();
    };

    private readonly getData = (state : IGeoServerBlockAutocompleteState, props : IGeoServerBlockAutocompleteProps) => props.blocks;
    private readonly getValue = (state : IGeoServerBlockAutocompleteState, props : IGeoServerBlockAutocompleteProps) => props.value;
    private readonly getRequired = (state : IGeoServerBlockAutocompleteState, props : IGeoServerBlockAutocompleteProps) => props.required;
    private readonly getDivisionGuid = (state : IGeoServerBlockAutocompleteState, props : IGeoServerBlockAutocompleteProps) => props.divisionGuid;
    private readonly getSubDivisionGuid = (state : IGeoServerBlockAutocompleteState, props : IGeoServerBlockAutocompleteProps) => props.subDivisionGuid;
    private readonly getDepartmentGuid = (state : IGeoServerBlockAutocompleteState, props : IGeoServerBlockAutocompleteProps) => props.departmentGuid;
    private readonly getFieldGuid = (state : IGeoServerBlockAutocompleteState, props : IGeoServerBlockAutocompleteProps) => props.fieldGuid;
    private readonly getCrop = (state : IGeoServerBlockAutocompleteState, props : IGeoServerBlockAutocompleteProps) => props.crop;
    private readonly getFieldName = (state : IGeoServerBlockAutocompleteState, props : IGeoServerBlockAutocompleteProps) => props.fieldName;
    private readonly getType = (state : IGeoServerBlockAutocompleteState, props : IGeoServerBlockAutocompleteProps) => props.type;
    private readonly getPoints = (state : IGeoServerBlockAutocompleteState, props : IGeoServerBlockAutocompleteProps) => props.points;
    private readonly getDivision = (state : IGeoServerBlockAutocompleteState, props : IGeoServerBlockAutocompleteProps) => props.division;
    
    private readonly getBlockAutocomplete = createSelector([
        this.getData,
        this.getRequired,
        this.getDivisionGuid,
        this.getSubDivisionGuid,
        this.getDepartmentGuid,
        this.getFieldGuid,
        this.getCrop,
        this.getFieldName,
        this.getType,
        this.getPoints,
        this.getDivision,
    ], (
        blocks,
        required,
        divisionGuid,
        subDivisionGuid,
        departmentGuid,
        fieldGuid,
        crop,
        fieldName,
        type,
        points,
        division,
    ) => {
        const blockGuids = lodash.chain(points)
            .filter(n => !type || n.type === type)
            .filter(n => !crop || n.crop === crop)
            .map(n => n.blockGuid)
            .uniq()
            .value();

        const blocksDrop = lodash.chain(blocks)
            .filter(n => (!crop && !type) || blockGuids.includes(n.guid))
            .filter(x => !divisionGuid || x.divisionGuid === divisionGuid)
            .filter(x => !division || x.departmentShortName.toLocaleLowerCase() === division.toLocaleLowerCase())
            .filter(x => !subDivisionGuid || x.subDivisionGuid === subDivisionGuid)
            .filter(x => !departmentGuid || x.departmentGuid === departmentGuid)
            .filter(x => !fieldGuid || x.fieldGuid === fieldGuid)
            .filter(x => !fieldName || x.fieldName === fieldName)
            .uniqBy(n => n.guid)
            .map(n => ({
                label: n.code,
                value: n.guid,
            }))
            .sortBy(n => n.label)
            .value();

        if (!required) {
            blocksDrop.unshift({
                label: 'ALL',
                value: '',
            });
        }

        return blocksDrop;
    });

    private readonly getSelectedValue = createSelector([
        this.getValue,
        this.getRequired,
        this.getBlockAutocomplete,
    ], (
        value,
        required,
        blocks,
    ) => {
        if (value && typeof(value) === 'string') {
            const block = blocks
                .find(x => x.value === value);

            return block ?? null;
        }

        if (!value && !required) {
            return {
                label: 'ALL',
                value: '',
            };
        }

        return null;
    });



    private readonly onChange = (event : React.ChangeEvent<unknown>, value : {
        label : string;
        value : string;
    } | null) => {
        const block = this.props.blocks
            .filter(x => !this.props.divisionGuid || x.divisionGuid === this.props.divisionGuid)
            .filter(x => !this.props.subDivisionGuid || x.subDivisionGuid === this.props.subDivisionGuid)
            .filter(x => !this.props.departmentGuid || x.departmentGuid === this.props.departmentGuid)
            .filter(x => !this.props.fieldGuid || x.fieldGuid === this.props.fieldGuid)
            .find(x => x.guid === value?.value);
        
        const bounds = !block ? null : GeoHelper.getPolygonBounds([block]);

        this.props.onChange(block, bounds, event);
    };

    public readonly render = () => {
        const {
            required,
            fullWidth,
            isLoading,
            disabled,
            label,
        } = this.props;

        const blocks = this.getBlockAutocomplete(this.state, this.props);

        const value = this.getSelectedValue(this.state, this.props);
        return (
            <React.Fragment>
                <FormControl fullWidth={fullWidth} error={required && !value} required={required}>
                    <Autocomplete
                        disabled={isLoading || disabled}
                        id='block_select'
                        options={blocks}
                        value={value}
                        getOptionSelected={(option, val) => option.value === val.value}
                        getOptionLabel={option => option.label}
                        onChange={this.onChange}
                        disableClearable={required}
                        openOnFocus
                        renderInput={params => (
                            <TextField
                                error={required && !value}
                                required={required}
                                {...params}
                                fullWidth={fullWidth}
                                label={label ?? 'Block'}
                            />)}
                    />
                    {
                        required && !value &&
                            <FormHelperText error>Required</FormHelperText>
                    }
                </FormControl>
                <div className='wfill' style={{
                    minHeight: 8,
                }}>
                    {
                        isLoading &&
                        <LinearProgress />
                    }
                </div>
            </React.Fragment>
        );
    };
}

const mapStateToProps = (state : IRootState) => {
    return {
        blocks: state.masterData.geoserver.blocks,
        points: state.masterData.geoserver.points,
        isLoading: state.masterData.geoserver.isLoadingBlocks || state.masterData.geoserver.isLoadingPoints,
    };
};

const GeoServerBlockAutocomplete = connect(
    mapStateToProps,
)(GeoServerBlockAutocompleteComponent);

export default GeoServerBlockAutocomplete;
